"use client";

import { createContext } from "react";
import type { FilterContextValue } from "./types";

/**
 * FilterContext is deliberately split into TWO React contexts.
 *
 * The provider's value used to be one object mixing referentially STABLE
 * members (`actions`, `helpers` — ~14 callbacks that never change identity
 * once mounted) with VOLATILE ones (`filters`, `uiState`, `computed` — new on
 * every keystroke in table search, because `setSearchTerm` writes
 * `filters.search` synchronously while only the GraphQL query is debounced).
 *
 * React context has no selector: any consumer of a combined value re-renders
 * when ANY member changes. So every table row — which only ever calls
 * `actions.setSimpleFilter` — re-rendered on every character typed.
 *
 * Splitting the contexts lets a consumer subscribe to only the half it reads.
 * `useFilterContext()` still composes both for call sites that want everything.
 */

/** The half that is stable for the lifetime of the provider. */
export interface FilterStableValue {
  actions: FilterContextValue["actions"];
  helpers: FilterContextValue["helpers"];
}

/** The half that changes as the user filters, searches, and toggles panels. */
export interface FilterStateValue {
  filters: FilterContextValue["filters"];
  uiState: FilterContextValue["uiState"];
  computed: FilterContextValue["computed"];
}

export const FilterStableContext = createContext<FilterStableValue | undefined>(undefined);

export const FilterStateContext = createContext<FilterStateValue | undefined>(undefined);
